import {
  AgnosticAttribute,
  AgnosticBreadcrumb,
  AgnosticMediaGalleryItem,
  AgnosticPrice,
  ProductGetters
} from '@vue-storefront/core';
import {
  ImageImageTypeEnum,
  Product
} from '@vsf-enterprise/sap-commerce-webservices-sdk';
import {
  getVariantName,
  getVariantOptions,
  imagesAgnosticBuilder
} from '../helpers';

interface ExtendedProductGetters<PRODUCT> extends Omit<ProductGetters<PRODUCT, any>, 'getFormattedPrice'>{
  getFormattedPrice: (product:Product) => string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getProductName = (product: Product): string => product?.name || '';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getProductSlug = (product: Product): string =>
  product?.code ? `/p/${product.code}/details` : '';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getProductPrice = (product: Product): AgnosticPrice => {
  // TODO: Check special price
  return {
    regular: product?.price?.value || 0,
    special: 0
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getProductGallery = (
  product: Product
): AgnosticMediaGalleryItem[] => {
  if (!product || !product.images?.length) {
    return [];
  }
  const galleryImages = product.images.filter(
    (image) => image.imageType === ImageImageTypeEnum.Gallery
  );

  if (galleryImages.length === 0) {
    return imagesAgnosticBuilder(product.images);
  }

  return imagesAgnosticBuilder(galleryImages);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getProductCoverImage = (product: Product): string => {
  if (!product || product?.images?.length === 0) {
    return '';
  }
  const { url } = product?.images.find(
    (image) =>
      image.imageType === ImageImageTypeEnum.Primary &&
      image.format === 'thumbnail'
  );
  return url;
};

// This getter is unnecessary for sap integration
export const getProductFiltered = (products: Product[]): Product[] => {
  return products;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getProductAttributes = (
  product: Product,
  filterByAttributeName?: string[]
): Record<string, AgnosticAttribute | string> => {
  const baseOptions: any =
    product?.baseOptions?.reduce((results, currentOption) => {
      const variantName = getVariantName(
        filterByAttributeName,
        currentOption?.variantType
      );
      results[variantName] = getVariantOptions(
        currentOption.options,
        variantName
      );
      return results;
    }, {}) || {};

  // Variant name for variantOptions
  const variantName = getVariantName(
    filterByAttributeName,
    product?.variantType
  );

  const variantOptions =
    product?.variantOptions?.reduce((results) => {
      results[variantName] = getVariantOptions(
        product?.variantOptions,
        variantName
      );
      return results;
    }, {}) || {};
  return { ...baseOptions, ...variantOptions };
};

export const getProductConfiguration = (
  product: Product,
  filterByAttributeName?: string[]
) => {
  const selectedBaseOptions =
    product?.baseOptions.reduce((results, currentOption) => {
      const variantName = getVariantName(
        filterByAttributeName,
        currentOption?.variantType
      );

      results[variantName] = currentOption.selected.code;

      return results;
    }, {}) || {};

  // Variant name for variantOptions
  const variantName = getVariantName(
    filterByAttributeName,
    product?.variantType
  );

  const selectedVariantOptions =
    product?.variantOptions?.reduce((results, currentOption) => {
      if (!results[variantName]) {
        results[variantName] = currentOption.code;
      }

      return results;
    }, {}) || {};

  return { ...selectedBaseOptions, ...selectedVariantOptions };
};

export const getProductDescription = (product: Product): string =>
  product?.description;

export const getProductCategoryIds = (product: Product): string[] =>
  product?.categories?.map((category) => category.code) || [];

export const getProductId = (product: Product): string => product?.code;

export const getFormattedPrice = (product: Product) => product?.price?.formattedValue || '';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getProductTotalReviews = (product: Product): number =>
  product?.numberOfReviews;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getProductAverageRating = (product: Product): number =>
  product?.averageRating;

export const getProductBreadcrumbs = (
  product: Product,
  homePageName?: string
): AgnosticBreadcrumb[] => {
  const categoriesBreadcrumb = product?.categories?.map(({ name, code }) => ({
    text: name,
    link: `/c/${code}/${name}`
  }));
  const homeBreadcrumb = {
    text: homePageName || 'Home',
    link: '/'
  };
  const productBreadcrumb = {
    text: product?.name,
    link: `/p/${product?.code}/details`
  };
  return [homeBreadcrumb, ...categoriesBreadcrumb, productBreadcrumb];
};

export const getProductIsInStock = (product: Pick<Product, 'stock'>): boolean =>
  product.stock?.stockLevelStatus !== 'outOfStock';

/**
 *  Returns agnostic product values.
 *  @example
 * Example of usage:
 * ```ts
 * const { search, products, loading, error } = useProduct('product-page')
 * await search({ id: '1043' }); // props are defined by UseProductFactoryProps interface
 * productGetters.getName(products.value) // should return agnostic name of a product
 * productGetters.getGallery(products.value) // should return agnostic gallery of images
 * ```
 */
export const productGetters: ExtendedProductGetters<Product> = {
  getName: getProductName,
  getSlug: getProductSlug,
  getPrice: getProductPrice,
  getGallery: getProductGallery,
  getCoverImage: getProductCoverImage,
  getFiltered: getProductFiltered,
  getAttributes: getProductAttributes,
  getDescription: getProductDescription,
  getCategoryIds: getProductCategoryIds,
  getId: getProductId,
  getFormattedPrice: getFormattedPrice,
  getTotalReviews: getProductTotalReviews,
  getAverageRating: getProductAverageRating,
  getConfiguration: getProductConfiguration,
  getBreadcrumbs: getProductBreadcrumbs,
  getIsInStock: getProductIsInStock
};
